import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Trophy, Gauge, Target, Activity, Flag, TrendingUp,
  TrendingDown, Hash, Loader2, AlertCircle, Sparkles,
  CloudRain, Cloud, Sun, Wind, Droplets, Thermometer, Gauge as GaugeIcon,
  ArrowUp, ArrowDown, Minus
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { API } from '../config/api';

const formatTime = (seconds) => {
  if (!seconds || seconds <= 0) return '—';
  const mins = Math.floor(seconds / 60);
  const secs = (seconds % 60).toFixed(3);
  return `${mins}:${secs.padStart(6, '0')}`;
};

const AwardCard = ({ icon: Icon, label, winner, accent = 'red', unit = 'time', testId }) => {
  const accents = {
    red: 'from-red-600/20 to-red-900/10 border-red-600/40 text-red-400',
    amber: 'from-amber-600/20 to-amber-900/10 border-amber-500/40 text-amber-400',
    cyan: 'from-cyan-600/20 to-cyan-900/10 border-cyan-500/40 text-cyan-400',
    emerald: 'from-emerald-600/20 to-emerald-900/10 border-emerald-500/40 text-emerald-400',
    violet: 'from-violet-600/20 to-violet-900/10 border-violet-500/40 text-violet-400',
    orange: 'from-orange-600/20 to-orange-900/10 border-orange-500/40 text-orange-400',
  };

  const displayValue = (() => {
    if (!winner) return '—';
    if (unit === 'time') return formatTime(winner.value);
    if (unit === 'seconds') return `${winner.value.toFixed(3)}s`;
    if (unit === 'percent') return `${winner.value.toFixed(2)}%`;
    return winner.value;
  })();

  return (
    <div
      data-testid={testId}
      className={`relative overflow-hidden rounded-lg border bg-gradient-to-br ${accents[accent]} p-4 transition-transform hover:-translate-y-0.5`}
    >
      <div className="flex items-start justify-between mb-2">
        <span className="text-[10px] uppercase tracking-[0.18em] text-gray-300 font-medium">{label}</span>
        <Icon className="w-4 h-4 opacity-70" />
      </div>
      {winner ? (
        <>
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-xl md:text-2xl font-bold text-white tabular-nums">{displayValue}</span>
            <Badge className="bg-black/40 text-gray-200 border-white/10 text-[10px] font-mono">
              #{winner.driver_number}
            </Badge>
          </div>
          <p className="text-sm text-white font-medium mt-1 truncate">{winner.driver_name}</p>
          {winner.detail && (
            <p className="text-[11px] text-gray-400 mt-1 leading-tight">{winner.detail}</p>
          )}
        </>
      ) : (
        <p className="text-gray-500 text-sm">Datos insuficientes</p>
      )}
    </div>
  );
};

const SessionReport = ({ raceId }) => {
  const [report, setReport] = useState(null);
  const [weather, setWeather] = useState(null);
  const [weatherError, setWeatherError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API}/races/${raceId}/session-report`);
        if (mounted) setReport(res.data);
      } catch (e) {
        if (mounted) setError(e.response?.data?.detail || 'Error al generar el reporte');
      } finally {
        if (mounted) setLoading(false);
      }
      // Weather is best-effort — failures don't block the report. If weather
      // returns 422 because the circuit wasn't detected, try reanalyze (server
      // re-downloads the Drive file and re-parses it) and retry.
      try {
        const wr = await axios.get(`${API}/races/${raceId}/weather`);
        if (mounted) setWeather(wr.data);
      } catch (e) {
        if (e.response?.status === 422) {
          try {
            await axios.post(`${API}/races/${raceId}/reanalyze-circuit`);
            const wr2 = await axios.get(`${API}/races/${raceId}/weather`);
            if (mounted) setWeather(wr2.data);
            return;
          } catch {
            /* fall through to error */
          }
        }
        if (mounted) setWeatherError(e.response?.data?.detail || 'No disponible');
      }
    })();
    return () => { mounted = false; };
  }, [raceId]);

  if (loading) {
    return (
      <Card className="bg-neutral-900 border-red-900/30">
        <CardContent className="py-16 flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-red-500 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  if (error || !report) {
    return (
      <Card className="bg-neutral-900 border-red-900/30">
        <CardContent className="py-12 text-center">
          <AlertCircle className="w-10 h-10 text-yellow-500 mx-auto mb-3" />
          <p className="text-gray-400">{error || 'No hay reporte disponible'}</p>
        </CardContent>
      </Card>
    );
  }

  const gap = Math.max(report.session_best_lap - report.session_theoretical_best, 0);

  return (
    <Card className="bg-neutral-900 border-red-900/30" data-testid="session-report">
      <CardHeader className="pb-4">
        <CardTitle className="text-xl text-white flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-red-500" />
          Reporte de Sesión
        </CardTitle>
        <CardDescription className="text-gray-400 text-sm">
          Resumen ejecutivo con los headline awards y métricas clave para el ingeniero de pista.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Cronometraje disclaimer banner */}
        <div
          data-testid="cronometraje-disclaimer"
          className="flex items-start gap-2.5 rounded-md border border-amber-700/40 bg-amber-950/30 px-3 py-2.5 text-amber-200/90"
        >
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-400" />
          <p className="text-xs leading-relaxed">
            <span className="font-semibold text-amber-300">Aviso:</span>{' '}
            Si encontrás algún error en los datos analizados, generalmente se debe a un error
            en el cronometraje oficial del evento. Los datos se procesan tal cual fueron publicados.
          </p>
        </div>

        {/* Session summary strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="rounded-md bg-neutral-800/60 border border-neutral-700/50 p-3">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Pilotos</div>
            <div className="text-lg font-semibold text-white tabular-nums">{report.total_drivers}</div>
          </div>
          <div className="rounded-md bg-neutral-800/60 border border-neutral-700/50 p-3">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Vtas competitivas</div>
            <div className="text-lg font-semibold text-white tabular-nums">{report.total_laps}</div>
          </div>
          <div className="rounded-md bg-neutral-800/60 border border-neutral-700/50 p-3">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Mejor vta (real)</div>
            <div className="text-lg font-semibold text-white tabular-nums">{formatTime(report.session_best_lap)}</div>
          </div>
          <div className="rounded-md bg-neutral-800/60 border border-neutral-700/50 p-3">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Vta teórica sesión</div>
            <div className="text-lg font-semibold text-white tabular-nums">{formatTime(report.session_theoretical_best)}</div>
            {gap > 0 && (
              <div className="text-[10px] text-amber-400 mt-0.5">
                −{gap.toFixed(3)}s vs. real
              </div>
            )}
          </div>
        </div>

        {/* Headline Awards */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Trophy className="w-4 h-4 text-amber-500" />
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider">Headline awards</h3>
          </div>
          <p className="text-[11px] text-gray-500 mb-3">
            Los cuatro números que todo ingeniero mira primero: quién marcó la vuelta más rápida,
            quién tiene la mejor vuelta teórica (suma de mejores parciales), quién es el más
            regular y quién sostuvo el mejor ritmo promedio.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <AwardCard
              icon={Flag}
              label="Mejor vuelta rápida"
              winner={report.fastest_lap}
              accent="red"
              unit="time"
              testId="award-fastest-lap"
            />
            <AwardCard
              icon={Target}
              label="Mejor vuelta óptima"
              winner={report.best_optimal}
              accent="amber"
              unit="time"
              testId="award-best-optimal"
            />
            <AwardCard
              icon={Activity}
              label="Mejor consistencia"
              winner={report.best_consistency}
              accent="cyan"
              unit="seconds"
              testId="award-best-consistency"
            />
            <AwardCard
              icon={Gauge}
              label="Mejor ritmo promedio"
              winner={report.best_average_pace}
              accent="emerald"
              unit="time"
              testId="award-best-pace"
            />
          </div>
        </div>

        {/* Sector Winners */}
        {report.sector_winners && report.sector_winners.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Hash className="w-4 h-4 text-red-500" />
              <h3 className="text-sm font-semibold text-white uppercase tracking-wider">Reyes del sector</h3>
            </div>
            <p className="text-[11px] text-gray-500 mb-3">
              El piloto que marcó el mejor tiempo absoluto en cada sector de la pista (no necesariamente
              el mismo que hizo la vuelta rápida).
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {report.sector_winners.map((sw) => (
                <div
                  key={sw.sector}
                  data-testid={`sector-winner-${sw.sector}`}
                  className="rounded-lg border border-red-900/40 bg-gradient-to-br from-neutral-800/60 to-neutral-900 p-3"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] uppercase tracking-widest text-gray-500">Sector {sw.sector}</span>
                    <Badge className="bg-red-600/20 text-red-300 border-red-500/30 text-[10px] font-mono">
                      #{sw.driver_number}
                    </Badge>
                  </div>
                  <div className="text-lg font-bold text-white tabular-nums">{sw.time.toFixed(3)}s</div>
                  <div className="text-sm text-gray-300 truncate">{sw.driver_name}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Advanced insights */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4 text-violet-400" />
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider">Insights de ingeniería</h3>
          </div>
          <p className="text-[11px] text-gray-500 mb-3">
            Cuánto aprovechó cada piloto su potencial: comparamos la vuelta real contra la vuelta teórica
            (suma de sus mejores parciales) y la estabilidad de sus sectores.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <AwardCard
              icon={TrendingDown}
              label="Mejor ejecución de potencial"
              winner={report.best_potential_execution}
              accent="emerald"
              unit="seconds"
              testId="award-best-execution"
            />
            <AwardCard
              icon={TrendingUp}
              label="Mayor margen de mejora"
              winner={report.biggest_potential_gap}
              accent="orange"
              unit="seconds"
              testId="award-biggest-gap"
            />
            <AwardCard
              icon={Activity}
              label="Más constante por sector"
              winner={report.most_sector_consistent}
              accent="violet"
              unit="percent"
              testId="award-most-sector-consistent"
            />
          </div>
        </div>

        {/* Weather */}
        <WeatherPanel
          weather={weather}
          error={weatherError}
          raceId={raceId}
          onLocationSet={async () => {
            try {
              const wr = await axios.get(`${API}/races/${raceId}/weather`);
              setWeather(wr.data);
              setWeatherError(null);
            } catch (e) {
              setWeatherError(e.response?.data?.detail || 'No disponible');
            }
          }}
        />

        {/* Trend analysis: degradation + progression */}
        <TrendPanel report={report} />

        {/* Sector strengths/weaknesses */}
        <SectorStrengthsPanel report={report} />

        <p className="text-[11px] text-gray-500 italic pt-2 border-t border-neutral-800">
          Cálculos realizados sobre vueltas competitivas (se excluyen vueltas &gt; 115% del personal best
          del piloto, p. ej. entradas a boxes o auto de seguridad).
        </p>
      </CardContent>
    </Card>
  );
};

// ---------- Sub-components ---------------------------------------------------

const WeatherPanel = ({ weather, error, raceId, onLocationSet }) => {
  const [manualText, setManualText] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const handleSave = async () => {
    const hint = manualText.trim();
    if (!hint) return;
    setSaving(true);
    setSaveError(null);
    try {
      // Ask backend to geocode this free-text hint
      await axios.patch(`${API}/races/${raceId}/location`, {
        circuit: `Autódromo ${hint}`,
        location: hint,
      });
      if (onLocationSet) onLocationSet();
    } catch (e) {
      setSaveError(e.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  if (!weather && !error) return null;
  if (!weather && error) {
    return (
      <div data-testid="weather-panel-empty" className="rounded-md border border-dashed border-neutral-700 p-4">
        <div className="flex items-center gap-2 mb-2">
          <Cloud className="w-4 h-4 text-gray-400" />
          <h3 className="text-sm font-semibold text-white uppercase tracking-wider">Clima de la sesión</h3>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          No encontramos el autódromo automáticamente. Escribí el <span className="text-gray-300">nombre del autódromo o ciudad</span>
          {' '}y buscamos el trazado y el clima histórico:
        </p>
        <form
          onSubmit={(e) => { e.preventDefault(); handleSave(); }}
          className="flex flex-col sm:flex-row gap-2"
        >
          <input
            data-testid="weather-circuit-input"
            type="text"
            value={manualText}
            onChange={(e) => setManualText(e.target.value)}
            placeholder="Ej: Rosario, Mouras, Termas de Río Hondo…"
            className="flex-1 bg-neutral-800 border border-neutral-700 text-gray-200 text-sm rounded px-3 py-2 focus:outline-none focus:border-red-500 placeholder:text-gray-600"
          />
          <button
            type="submit"
            data-testid="weather-save-circuit"
            disabled={!manualText.trim() || saving}
            className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm px-4 py-2 rounded transition-colors whitespace-nowrap"
          >
            {saving ? 'Buscando…' : 'Buscar clima'}
          </button>
        </form>
        {saveError && <p className="text-xs text-orange-400 mt-2">{saveError}</p>}
      </div>
    );
  }

  const Icon = (() => {
    const c = (weather.weather_condition || '').toLowerCase();
    if (c.includes('tormenta')) return CloudRain;
    if (c.includes('lluvia') || c.includes('chaparron') || c.includes('llovizna')) return CloudRain;
    if (c.includes('nub')) return Cloud;
    return Sun;
  })();

  const Metric = ({ icon: I, label, value, sub }) => (
    <div className="rounded-md bg-neutral-800/60 border border-neutral-700/50 p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-gray-500 mb-1">
        <I className="w-3 h-3" /> {label}
      </div>
      <div className="text-base md:text-lg font-semibold text-white tabular-nums">{value}</div>
      {sub && <div className="text-[10px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );

  return (
    <div data-testid="weather-panel">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-sky-400" />
        <h3 className="text-sm font-semibold text-white uppercase tracking-wider">Clima de la sesión</h3>
        {weather.weather_condition && (
          <Badge className="bg-sky-500/15 text-sky-300 border-sky-500/30 text-[10px]">
            {weather.weather_condition}
          </Badge>
        )}
        {weather.rained_during_session && (
          <Badge className="bg-blue-600/20 text-blue-300 border-blue-500/30 text-[10px]">
            Llovió durante la sesión
          </Badge>
        )}
      </div>
      <p className="text-[11px] text-gray-500 mb-1">
        Datos meteorológicos tomados del <span className="text-gray-400">archivo histórico</span> del día y hora
        exactos de la sesión (Open-Meteo). La <span className="text-gray-400">temperatura de pista</span> se estima
        a partir del ambiente y la nubosidad (a más sol, más hot track).
      </p>
      <p className="text-[11px] text-gray-500 mb-3">
        {weather.circuit || weather.location || 'Autódromo'} ·{' '}
        {weather.date}{weather.session_time ? ` · ${weather.session_time}` : ''}
      </p>

      {/* Brújula del viento (sin radar en vivo: reporte histórico) */}
      <div className="mb-3">
        <WindCompass
          directionDeg={weather.wind_direction_deg}
          cardinal={weather.wind_direction_cardinal}
          speedKmh={weather.wind_speed_kmh}
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        <Metric
          icon={Thermometer} label="Temp. ambiente"
          value={weather.temperature_c != null ? `${weather.temperature_c.toFixed(1)}°C` : '—'}
          sub={weather.temp_min_c != null ? `Mín ${weather.temp_min_c.toFixed(0)}° / Máx ${weather.temp_max_c?.toFixed(0)}°` : null}
        />
        <Metric
          icon={Thermometer} label="Temp. pista (est.)"
          value={weather.track_temp_estimate_c != null ? `${weather.track_temp_estimate_c.toFixed(1)}°C` : '—'}
          sub={weather.feels_like_c != null ? `Sensación ${weather.feels_like_c.toFixed(1)}°C` : null}
        />
        <Metric
          icon={Droplets} label="Humedad"
          value={weather.humidity_pct != null ? `${weather.humidity_pct.toFixed(0)}%` : '—'}
        />
        <Metric
          icon={Wind} label="Viento"
          value={weather.wind_speed_kmh != null ? `${weather.wind_speed_kmh.toFixed(1)} km/h` : '—'}
          sub={weather.wind_direction_cardinal ? `Dirección ${weather.wind_direction_cardinal}` : null}
        />
        <Metric
          icon={GaugeIcon} label="Presión"
          value={weather.pressure_hpa != null ? `${weather.pressure_hpa.toFixed(0)} hPa` : '—'}
          sub={weather.cloud_cover_pct != null ? `Nubosidad ${weather.cloud_cover_pct.toFixed(0)}%` : null}
        />
      </div>
    </div>
  );
};

const TrendPanel = ({ report }) => {
  const hasData = (report.degradation_table && report.degradation_table.length > 0) ||
                  (report.progression_table && report.progression_table.length > 0);
  if (!hasData) return null;

  return (
    <div data-testid="trend-panel">
      <div className="flex items-center gap-2 mb-1">
        <TrendingDown className="w-4 h-4 text-orange-400" />
        <h3 className="text-sm font-semibold text-white uppercase tracking-wider">Degradación y progresión</h3>
      </div>
      <p className="text-[11px] text-gray-500 mb-3">
        <span className="text-gray-400">Slope/vta</span>: segundos que el piloto gana o pierde <em>por vuelta</em>
        (regresión lineal sobre sus vueltas competitivas). <span className="text-emerald-400">Negativo</span> = mejora progresiva,
        <span className="text-orange-400"> positivo</span> = gomas cayendo, tráfico o cansancio.
        <br />
        <span className="text-gray-400">Δ 1ª vs 2ª mitad</span>: cuánto mejoró (↓) o empeoró (↑) el promedio de
        la segunda mitad de la sesión respecto a la primera.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <AwardCard
          icon={ArrowDown}
          label="Menor degradación"
          winner={report.best_degradation}
          accent="emerald"
          unit="seconds"
          testId="award-best-degradation"
        />
        <AwardCard
          icon={ArrowUp}
          label="Mayor degradación"
          winner={report.worst_degradation}
          accent="orange"
          unit="seconds"
          testId="award-worst-degradation"
        />
        <AwardCard
          icon={TrendingUp}
          label="Mejor progresión"
          winner={report.best_progression}
          accent="cyan"
          unit="seconds"
          testId="award-best-progression"
        />
      </div>

      {report.degradation_table.length > 0 && (
        <div className="rounded-lg border border-neutral-800 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-neutral-900/70 text-gray-400 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Piloto</th>
                <th className="text-right px-3 py-2 font-medium">Vtas</th>
                <th className="text-right px-3 py-2 font-medium">Slope/vta</th>
                <th className="text-right px-3 py-2 font-medium">Δ 1ª vs 2ª mitad</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-800">
              {report.degradation_table
                .slice()
                .sort((a, b) => a.slope_per_lap - b.slope_per_lap)
                .map((d) => {
                  const prog = (report.progression_table || []).find(p => p.driver_number === d.driver_number);
                  const slopeColor =
                    d.slope_per_lap < -0.05 ? 'text-emerald-400' :
                    d.slope_per_lap >  0.2  ? 'text-orange-400'  :
                    'text-gray-300';
                  const improvement = prog?.improvement;
                  return (
                    <tr key={d.driver_number} className="hover:bg-neutral-800/30">
                      <td className="px-3 py-2 text-white">
                        <span className="font-mono text-gray-500 mr-2">#{d.driver_number}</span>
                        {d.driver_name}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-400 tabular-nums">{d.laps_analyzed}</td>
                      <td className={`px-3 py-2 text-right font-semibold tabular-nums ${slopeColor}`}>
                        {d.slope_per_lap > 0 ? '+' : ''}{d.slope_per_lap.toFixed(3)}s
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {improvement !== undefined ? (
                          <span className={improvement > 0 ? 'text-emerald-400' : improvement < 0 ? 'text-orange-400' : 'text-gray-400'}>
                            {improvement > 0 ? <ArrowDown className="w-3 h-3 inline mr-0.5" /> :
                             improvement < 0 ? <ArrowUp className="w-3 h-3 inline mr-0.5" /> :
                             <Minus className="w-3 h-3 inline mr-0.5" />}
                            {Math.abs(improvement).toFixed(3)}s
                          </span>
                        ) : <span className="text-gray-600">—</span>}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const SectorStrengthsPanel = ({ report }) => {
  const data = report.sector_strengths || [];
  if (data.length === 0) return null;
  const sectorCount = data[0].sector_gaps.length;

  return (
    <div data-testid="sector-strengths-panel">
      <div className="flex items-center gap-2 mb-1">
        <Target className="w-4 h-4 text-amber-400" />
        <h3 className="text-sm font-semibold text-white uppercase tracking-wider">Fortalezas y debilidades por sector</h3>
      </div>
      <p className="text-[11px] text-gray-500 mb-3">
        Para cada piloto, cuánto tiempo pierde en cada sector respecto al
        <span className="text-emerald-400"> mejor tiempo absoluto</span> de ese sector en toda la sesión.
        <span className="text-emerald-400"> 0.000s / S verde</span> = es el referente en ese sector ·
        <span className="text-orange-400"> S naranja</span> = ahí está su mayor pérdida.
      </p>
      <div className="rounded-lg border border-neutral-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-900/70 text-gray-400 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Piloto</th>
              {Array.from({ length: sectorCount }).map((_, i) => (
                <th key={i} className="text-right px-3 py-2 font-medium">Gap S{i + 1}</th>
              ))}
              <th className="text-right px-3 py-2 font-medium">Fortaleza</th>
              <th className="text-right px-3 py-2 font-medium">Debilidad</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-800">
            {data
              .slice()
              .sort((a, b) => {
                const aSum = a.sector_gaps.reduce((x, y) => x + y, 0);
                const bSum = b.sector_gaps.reduce((x, y) => x + y, 0);
                return aSum - bSum;
              })
              .map((d) => (
                <tr key={d.driver_number} className="hover:bg-neutral-800/30">
                  <td className="px-3 py-2 text-white">
                    <span className="font-mono text-gray-500 mr-2">#{d.driver_number}</span>
                    {d.driver_name}
                  </td>
                  {d.sector_gaps.map((g, i) => (
                    <td key={i} className={`px-3 py-2 text-right tabular-nums ${
                      g === 0 ? 'text-emerald-400 font-semibold' :
                      g > 0.3 ? 'text-orange-400' :
                      'text-gray-300'
                    }`}>
                      {g === 0 ? '—' : `+${g.toFixed(3)}s`}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right">
                    <Badge className="bg-emerald-600/20 text-emerald-300 border-emerald-500/30 text-[10px]">
                      S{d.strongest_sector}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Badge className="bg-orange-600/20 text-orange-300 border-orange-500/30 text-[10px]">
                      S{d.weakest_sector}
                    </Badge>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SessionReport;

// ---------- Wind compass ---------------------------------------------------

const WindCompass = ({ directionDeg, cardinal, speedKmh }) => {
  if (directionDeg == null) return null;
  // SVG: arrow points TO where the wind blows (meteorological convention:
  // `wind_direction_deg` = direction wind blows FROM, so we invert by 180°).
  const rot = (directionDeg + 180) % 360;

  return (
    <div
      data-testid="wind-compass"
      className="rounded-lg border border-neutral-700/60 bg-neutral-900 p-3 flex flex-col items-center justify-center min-w-[180px]"
    >
      <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">Viento</div>
      <svg viewBox="0 0 120 120" className="w-32 h-32">
        {/* Outer ring */}
        <circle cx="60" cy="60" r="54" fill="none" stroke="#374151" strokeWidth="1.5" />
        <circle cx="60" cy="60" r="44" fill="none" stroke="#1f2937" strokeWidth="1" />
        {/* Cardinal ticks */}
        {[0, 90, 180, 270].map((a) => (
          <line
            key={a}
            x1="60" y1="8" x2="60" y2="16"
            stroke="#4b5563" strokeWidth="2"
            transform={`rotate(${a} 60 60)`}
          />
        ))}
        {/* Intermediate ticks */}
        {[45, 135, 225, 315].map((a) => (
          <line
            key={a}
            x1="60" y1="10" x2="60" y2="14"
            stroke="#374151" strokeWidth="1"
            transform={`rotate(${a} 60 60)`}
          />
        ))}
        {/* Labels */}
        <text x="60" y="6" textAnchor="middle" fill="#ef4444" fontSize="9" fontWeight="700">N</text>
        <text x="115" y="63" textAnchor="middle" fill="#9ca3af" fontSize="8">E</text>
        <text x="60" y="118" textAnchor="middle" fill="#9ca3af" fontSize="8">S</text>
        <text x="5" y="63" textAnchor="middle" fill="#9ca3af" fontSize="8">O</text>
        {/* Arrow */}
        <g transform={`rotate(${rot} 60 60)`}>
          <line x1="60" y1="82" x2="60" y2="26" stroke="#38bdf8" strokeWidth="3" strokeLinecap="round" />
          <polygon points="60,18 54,32 66,32" fill="#38bdf8" />
          <circle cx="60" cy="60" r="4" fill="#0ea5e9" />
        </g>
      </svg>
      <div className="text-lg font-semibold text-white tabular-nums mt-1">
        {speedKmh != null ? `${speedKmh.toFixed(1)}` : '—'}
        <span className="text-xs text-gray-500 ml-1">km/h</span>
      </div>
      <div className="text-[11px] text-gray-400">
        {cardinal ? `Desde el ${cardinal}` : '—'}
        {directionDeg != null && (
          <span className="text-gray-600 ml-1">· {Math.round(directionDeg)}°</span>
        )}
      </div>
    </div>
  );
};
